import React, { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useCommonTranslation } from '../../modules/i18n';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  headerContent?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  height?: 'sm' | 'md' | 'lg' | 'xl' | 'auto';
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, footer, headerContent, size = 'md', height = 'md' }) => {
  const { t } = useCommonTranslation();
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'w-full max-w-none mx-3 sm:max-w-md sm:mx-auto',
    md: 'w-full max-w-none mx-3 sm:max-w-xl md:max-w-2xl sm:mx-auto',
    lg: 'w-full max-w-none mx-3 sm:max-w-xl md:max-w-3xl lg:max-w-4xl sm:mx-auto',
    xl: 'w-full max-w-none mx-3 sm:max-w-xl md:max-w-4xl lg:max-w-6xl sm:mx-auto'
  };

  const heightClasses = {
    sm: 'h-[30vh]',
    md: 'h-[50vh]',
    lg: 'h-[85vh]',
    xl: 'h-[90vh]',
    auto: 'h-auto'
  };

  return createPortal(
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70'
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`w-full bg-white dark:bg-dark-background-secondary ${sizeClasses[size]} flex flex-col rounded-lg shadow-xl dark:shadow-2xl ${heightClasses[height]} overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Sticky */}
        {(title || headerContent) && (
          <div className='flex-shrink-0 rounded-t-lg border-b border-gray-200 bg-white dark:border-border-dark dark:bg-dark-background-secondary'>
            {/* Title and Close Button */}
            {title && (
              <div className='flex items-center justify-between p-3 sm:p-4'>
                <h2 className='truncate pr-4 text-lg font-semibold text-gray-900 dark:text-dark-text sm:text-xl'>{title}</h2>
                <button
                  onClick={onClose}
                  className='flex-shrink-0 text-gray-500 hover:text-gray-700 dark:text-dark-text-secondary dark:hover:text-dark-text'
                  aria-label={t('actions.close')}
                >
                  <svg className='h-6 w-6' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
                    <path
                      fillRule='evenodd'
                      d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                      clipRule='evenodd'
                    />
                  </svg>
                </button>
              </div>
            )}

            {/* Custom Header Content (Tabs, etc.) */}
            {headerContent && <div className={title ? '' : 'p-3 sm:p-4'}>{headerContent}</div>}
          </div>
        )}

        {/* Content - Scrollable */}
        <div className={`flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 ${title || headerContent ? 'p-3 sm:p-4' : ''}`}>{children}</div>

        {/* Footer - Sticky */}
        {footer && (
          <div className='flex-shrink-0 rounded-b-lg border-t border-gray-200 bg-white p-3 dark:border-border-dark dark:bg-dark-background-secondary sm:p-4'>
            {footer}
          </div>
        )}
      </div>
    </div>,
    (document.fullscreenElement as HTMLElement) || document.body
  );
};
